// QualityInspectorProfile.jsx
import React, { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { Tabs } from 'antd';
import CommonService from "../../services/common.service";
import { toast } from 'react-toastify';
import '../../assets/styles/seller_profile.scss';
import { useLocationMaster } from "../../hooks/useLocationMaster";
import { useBusinessTypeMaster } from "../../hooks/useBusinessTypeMaster";
import { API_BASE_URL } from "../../constants/api-urls";
import StorageService from "../../services/StorageService";
import { getProfileRoute } from "../../utils/getProfileRoute";


// Initial state for Identity & Summary (Tab 1)
const initialPersonalDetails = {
  name: '',
  profile_type: 'Individual Professional',
  company_name: '',
  languages: [],
  country: '',
  state: '',
  city: '',
  pincode: '',
  address: '',
  email: '',
  phone: '',
  alternate_contact: '',
  preferred_method: 'WhatsApp',
  time_zone: '',
  professional_headline: '',
  bio: '',
  current_status: 'Available',
  radius: '',
  geographic_work_limit: 'State',
  primary_port_focus: '',
  multi_location_audits: '',
  gst_no: '',
};

// Initial state for Expertise & Compliance (Tab 2)
const initialOtherInfo = {
  primary_specialization: [],
  sub_specialties: [],
  specializations: [], // Array of selected specialization tags
  technical_specializations: '',
  industries_served: [],
  inspection_types: [],
  quality_methodologies: '',
  calibrated_tools_portfolio: '',
  notable_clients: '',
  services_offered: {
    inspection_type: [],
    industries_served: [],
    methodologies: [],
    notable_projects: []
  },
  experience: [{
    role_name: '',
    level: 'Mid Level',
    year_of_experience: '',
    notable_projects_clients: ''
  }],
  education: [{
    degree: '',
    specialization: '',
    institution: '',
    year: ''
  }],
  certifications: [{
    cert_name: '',
    issuer: '',
    valid_until: '',
    attachment: ''
  }],
  training_programs: [],
  testimonials: [{
    details: '',
    attachment: ''
  }],
  internal_status: 'PENDING SUBMISSION',
  government_id_proof: '',
  address_proof: '',
  tax_id: '',
  accept_nda: false,
  accept_ethical_conduct: false,
  pricing_model: 'Per Day (Monday)',
  settlement_currency: 'USD - US Dollar ($)',
  payout_frequency: 'Weekly Settlement',
  routing_iban_details: '',
  iban_account_number: '',
  swift_bic_code: '',
  ifsc_swift_bic: '',
  account_holder_name: '',
  bank_name: '',
  branch_name: '',
  ifsc_code: '',
};

export default function QualityInspectorProfile() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialPersonalDetails);
  const [otherInfo, setOtherInfo] = useState(initialOtherInfo);
  const [activeTab, setActiveTab] = useState('1');
  const [qualityInspectorId, setQualityInspectorId] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Check if user has Quality Inspector role based on user_type_id = 4 - redirect if not
  useEffect(() => {
    const user = StorageService.getUser();
    if (!user) {
      navigate('/login');
      return;
    }

    // Check if user has Quality Inspector role based on user_type_id in user_login table
    // Quality Inspector has user_type_id = 4 in master_user_type table
    let userTypeId = user.user_type_id;
    if (userTypeId === null || userTypeId === undefined) {
      userTypeId = '';
    } else {
      userTypeId = String(userTypeId).trim();
    }
    const typeIds = userTypeId 
      ? userTypeId.split(',').map(id => String(id).trim()).filter(id => id && id !== 'null' && id !== 'undefined')
      : [];
    
    // Quality Inspector has user_type_id = 4
    const hasQualityInspectorRole = typeIds.includes('4') || userTypeId === '4';

    // If user doesn't have Quality Inspector role (user_type_id = 4), redirect to their appropriate profile page
    if (!hasQualityInspectorRole) {
      const profileRoute = getProfileRoute();
      if (profileRoute !== '/quality-inspector-profile') {
        toast.warning('You do not have access to Quality Inspector profile. Redirecting to your profile page.');
        navigate(profileRoute);
      }
    }
  }, [navigate]);
  
  // File upload refs
  const profilePictureRef = useRef(null);
  const resumeRef = useRef(null);
  const governmentIdProofRef = useRef(null);
  const addressProofRef = useRef(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [resumeUrl, setResumeUrl] = useState('');
  const [governmentIdProofUrl, setGovernmentIdProofUrl] = useState('');
  const [addressProofUrl, setAddressProofUrl] = useState('');
  
  // Helper state for language with proficiency
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedProficiency, setSelectedProficiency] = useState('Basic');
  
  // Master Data Hooks
  const {
    countries: rawCountries,
    states: rawStates,
    cities: rawCities,
    updateListsForCountry,
    updateListsForState,
  } = useLocationMaster();
  
  const { timezoneOptions } = useBusinessTypeMaster();

  const countryOptions = rawCountries.map(c => ({ value: c.id, label: c.label }));
  const stateOptions = rawStates.map(s => ({ value: s.id, label: s.label }));
  const cityOptions = rawCities.map(c => ({ value: c.id, label: c.label }));

  // Language options with proficiency levels
  const languageOptions = [
    { value: 1, label: 'English' },
    { value: 2, label: 'Hindi' },
    { value: 3, label: 'Spanish' },
    { value: 4, label: 'French' },
    { value: 5, label: 'German' },
    { value: 6, label: 'Chinese' },
    { value: 7, label: 'Japanese' },
    { value: 8, label: 'Arabic' },
    { value: 9, label: 'Mandarin' },
  ];

  const proficiencyOptions = [
    { value: 'Native', label: 'Native' },
    { value: 'Fluent', label: 'Fluent' },
    { value: 'Professional', label: 'Professional' },
    { value: 'Intermediate', label: 'Intermediate' },
    { value: 'Basic', label: 'Basic' },
  ];

  // Profile type options
  const profileTypeOptions = [
    { value: 'Individual Professional', label: 'Individual Professional' },
    { value: 'Company', label: 'Company' },
  ];

  // Geographic work limit options
  const geographicWorkLimitOptions = [
    { value: 'State', label: 'State' },
    { value: 'Country', label: 'Country' },
    { value: 'Regional', label: 'Regional' },
    { value: 'Global', label: 'Global' },
  ];

  // Time zone options - using timezone map from API
  // timezoneOptions comes from useBusinessTypeMaster hook

  // Preferred method options
  const preferredMethodOptions = [
    { value: 'WhatsApp', label: 'WhatsApp' },
    { value: 'Email', label: 'Email' },
    { value: 'Phone', label: 'Phone' },
    { value: 'SMS', label: 'SMS' },
  ];

  // Current status options
  const currentStatusOptions = [
    { value: 'Available', label: 'Available' },
    { value: 'Busy', label: 'Busy' },
    { value: 'Unavailable', label: 'Unavailable' },
  ];

  // Industries served options (for button selection)
  const industriesServedButtonOptions = [
    'Electronics', 'Apparel', 'Toys', 'Furnitures', 'Automotive', 'Medical Devices', 'Renewable Energy'
  ];

  // Inspection types options (for button selection)
  const inspectionTypesButtonOptions = [
    'PRE-SHIPMENT (PSI)', 'IN-PROCESS (DUPRO)', 'FINAL RANDOM (FRI)', 'FACTORY AUDIT (FAD)', 'CONTAINER LOADING (CL)'
  ];

  // Level options (for experience)
  const levelOptions = [
    { value: 'Entry Level', label: 'Entry Level' },
    { value: 'Mid Level', label: 'Mid Level' },
    { value: 'Senior Auditor', label: 'Senior Auditor' },
    { value: 'Expert', label: 'Expert' },
  ];

  // Primary specialization options (for dropdown)
  const primarySpecializationDropdownOptions = [
    { value: 'Electronics & High-Tech', label: 'Electronics & High-Tech' },
    { value: 'Textiles & Apparel', label: 'Textiles & Apparel' },
    { value: 'Food & Beverage', label: 'Food & Beverage' },
    { value: 'Pharmaceuticals', label: 'Pharmaceuticals' },
    { value: 'Automotive', label: 'Automotive' },
    { value: 'Toys & Games', label: 'Toys & Games' },
    { value: 'Furniture', label: 'Furniture' },
    { value: 'Medical Devices', label: 'Medical Devices' },
  ];

  // Pricing model options
  const pricingModelOptions = [
    { value: 'Per Day (Monday)', label: 'Per Day (Monday)' },
    { value: 'Per Hour', label: 'Per Hour' },
    { value: 'Per Project', label: 'Per Project' },
    { value: 'Fixed', label: 'Fixed' },
  ];

  // Settlement currency options
  const settlementCurrencyOptions = [
    { value: 'USD - US Dollar ($)', label: 'USD - US Dollar ($)' },
    { value: 'EUR - Euro (€)', label: 'EUR - Euro (€)' },
    { value: 'GBP - British Pound (£)', label: 'GBP - British Pound (£)' },
    { value: 'INR - Indian Rupee (₹)', label: 'INR - Indian Rupee (₹)' },
    { value: 'CNY - Chinese Yuan (¥)', label: 'CNY - Chinese Yuan (¥)' },
  ];

  // Payout frequency options
  const payoutFrequencyOptions = [
    { value: 'Weekly Settlement', label: 'Weekly Settlement' },
    { value: 'Bi-weekly Settlement', label: 'Bi-weekly Settlement' },
    { value: 'Monthly Settlement', label: 'Monthly Settlement' },
    { value: 'Per Project', label: 'Per Project' },
  ];

  const subSpecializationOptions = [
    { value: 1, label: 'Pre-shipment Inspection' },
    { value: 2, label: 'During Production Inspection' },
    { value: 3, label: 'Final Random Inspection' },
    { value: 4, label: 'Container Loading Inspection' },
  ];

  // Specialization options for pill tags
  const specializationOptions = [
    'Product Inspection',
    'Process Audit',
    'Pre-shipment Inspection',
    'Final Random Inspection',
    'Container Loading Inspection',
    'During Production Inspection'
  ];

  // Training Programs options for multi-select
  const trainingProgramOptions = [
    { value: 'ISO 9001 Training', label: 'ISO 9001 Training' },
    { value: 'Quality Management', label: 'Quality Management' },
    { value: 'Six Sigma Certification', label: 'Six Sigma Certification' },
    { value: 'Lean Manufacturing', label: 'Lean Manufacturing' },
    { value: 'Statistical Process Control', label: 'Statistical Process Control' }
  ];

  // Services Offered options
  const inspectionTypeOptions = [
    { value: 1, label: 'Visual Inspection' },
    { value: 2, label: 'Dimensional Inspection' },
    { value: 3, label: 'Functional Testing' },
    { value: 4, label: 'Packaging Inspection' },
  ];

  const methodologiesOptions = [
    { value: 1, label: 'AQL Sampling' },
    { value: 2, label: '100% Inspection' },
    { value: 3, label: 'Statistical Process Control' },
    { value: 4, label: 'Six Sigma' },
  ];

  const industriesServedOptions = [
    { value: 1, label: 'Electronics' },
    { value: 2, label: 'Textiles' },
    { value: 3, label: 'Food & Beverage' },
    { value: 4, label: 'Pharmaceuticals' },
    { value: 5, label: 'Automotive' },
  ];

  const notableProjectsOptions = [
    { value: 1, label: 'Project A' },
    { value: 2, label: 'Project B' },
    { value: 3, label: 'Project C' },
  ];

  // Degree type options
  const degreeTypeOptions = [
    { value: 'Bachelor', label: 'Bachelor' },
    { value: 'Master', label: 'Master' },
    { value: 'PhD', label: 'PhD' },
    { value: 'Diploma', label: 'Diploma' },
    { value: 'Certificate', label: 'Certificate' },
  ];

  // Experience level options
  const experienceLevelOptions = [
    { value: 'Entry Level', label: 'Entry Level' },
    { value: 'Mid Level', label: 'Mid Level' },
    { value: 'Senior Level', label: 'Senior Level' },
    { value: 'Expert', label: 'Expert' },
  ];


  const findOption = (options, val) => {
    if (!val || !options || options.length === 0) return null;
    return options.find((o) => 
      String(o.value) === String(val) || 
      Number(o.value) === Number(val) ||
      o.value === val
    ) || null;
  };

  // Helper function to extract file name from path
  const getFileNameFromPath = (filePath) => {
    if (!filePath) return null;
    
    // Remove query parameters if any
    const pathWithoutQuery = filePath.split('?')[0];
    
    // Extract just the fileName part (last segment after /)
    const parts = pathWithoutQuery.split('/');
    const fileName = parts[parts.length - 1];
    
    // Return fileName if it exists and is not empty
    return fileName && fileName.trim() !== '' ? fileName : null;
  };

  // Load existing profile
  useEffect(() => {
    loadExistingProfile();
  }, []);

  const loadExistingProfile = async () => {
    try {
      setIsLoadingProfile(true);
      const response = await CommonService.get('/quality-inspector/my-profile');
      
      if (response && response.success && response.data) {
        const profile = response.data;
        setQualityInspectorId(profile.quality_inspector_id);
        
        // Load location data FIRST (before setting form state) to ensure dropdowns are populated
        if (profile.country_id) {
          // Load states for the country
          await updateListsForCountry(profile.country_id);
          // If state is set, load cities for that state
          if (profile.state_id) {
            await updateListsForState(profile.country_id, profile.state_id);
          }
          // Small delay to ensure React has updated the options arrays in state
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        // Load Personal Details AFTER location data is loaded
        // This ensures the Select components can find the matching options
        // Parse profile_data if it's a string
        let profileData = profile.profile_data;
        if (typeof profileData === 'string') {
          try {
            profileData = JSON.parse(profileData);
          } catch (e) {
            console.error('Error parsing profile_data:', e);
            profileData = null;
          }
        }
        
        // Parse bank_details if it's a string
        let bankDetails = profile.bank_details;
        if (typeof bankDetails === 'string') {
          try {
            bankDetails = JSON.parse(bankDetails);
          } catch (e) {
            console.error('Error parsing bank_details:', e);
            bankDetails = null;
          }
        }

        // Parse languages - handle both old format (array of IDs) and new format (array of objects)
        let languages = [];
        if (profile.language_id_str) {
          const langIds = profile.language_id_str.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
          // If profile_data has language proficiencies, use them; otherwise default to Basic
          if (profileData?.languages && Array.isArray(profileData.languages)) {
            languages = profileData.languages;
          } else {
            languages = langIds.map(id => ({ id, proficiency: 'Basic' }));
          }
        }

        setForm({
          name: profile.name || '',
          profile_type: profileData?.profile_type || 'Individual',
          company_name: profile.company_name || '',
          languages: languages,
          country: profile.country_id || '',
          state: profile.state_id || '',
          city: profile.city_id || '',
          state_text: profileData?.state_text || '',
          city_text: profileData?.city_text || '',
          pincode: profile.pincode ? String(profile.pincode) : '',
          address: profile.address || '',
          time_zone: profileData?.time_zone || '',
          email: profile.email || '',
          phone: profile.phone || '',
          alternate_contact: profile.alternate_contact || '',
          preferred_method: profileData?.preferred_method || 'WhatsApp',
          professional_headline: profileData?.professional_headline || '',
          bio: profile.bio || '',
          current_status: profileData?.current_status || 'Available',
          radius: profileData?.radius || '',
          gst_no: profile.gst_no || '',
        });

        // Load profile picture and resume URLs if they exist
        // Store relative paths in state (not full URLs) for consistency
        // Helper to get relative path from access path or fileName
        const getRelativePath = (accessPath, fileName) => {
          if (!accessPath && !fileName) return '';
          
          // If accessPath exists and is a string, convert to relative path if it's a full URL
          if (accessPath && typeof accessPath === 'string') {
            // Remove base URL if present
            let relativePath = accessPath.replace(/^https?:\/\/[^\/]+/, '');
            // Ensure it starts with /
            if (!relativePath.startsWith('/')) {
              relativePath = `/${relativePath}`;
            }
            return relativePath;
          }
          
          // If only fileName exists, construct relative path from company name
          // Note: Backend uses 'quality' category, so folder is media/quality/{companyName}
          if (fileName && typeof fileName === 'string') {
            const companyName = (profile.company_name || profile.name || 'quality_inspector').replace(/[^a-zA-Z0-9]/g, '_');
            return `/media/quality/${companyName}/${fileName}`;
          }
          
          return '';
        };

        // Load profile picture - reset if not present
        if (profile.profile_picture || profile.profile_picture_file_name) {
          // Store relative path in state
          const picturePath = getRelativePath(profile.profile_picture, profile.profile_picture_file_name);
          setProfilePictureUrl(picturePath);
        } else {
          // Reset if no profile picture in response
          setProfilePictureUrl('');
        }
        
        // Load resume - reset if not present
        if (profile.resume || profile.resume_file_name) {
          // Store relative path in state
          const resumePath = getRelativePath(profile.resume, profile.resume_file_name);
          setResumeUrl(resumePath);
        } else {
          // Reset if no resume in response
          setResumeUrl('');
        }

        // Load Other Information (Second Tab Data)
        // Helper to normalize file paths (convert full URLs to relative paths)
        const normalizeFilePath = (filePath) => {
          if (!filePath) return '';
          // If it's already a relative path, return as is
          if (filePath.startsWith('/')) {
            return filePath;
          }
          // If it's a full URL, convert to relative path
          if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
            const url = new URL(filePath);
            return url.pathname;
          }
          // Otherwise, ensure it starts with /
          return filePath.startsWith('/') ? filePath : `/${filePath}`;
        };

        if (profileData && typeof profileData === 'object' && Object.keys(profileData).length > 0) {
          setOtherInfo({
            // Handle primary_specialization - convert string to array if needed
            // Handle primary_specialization - convert string to array if needed
            primary_specialization: Array.isArray(profileData.primary_specialization)
              ? profileData.primary_specialization
              : (profileData.primary_specialization ? (typeof profileData.primary_specialization === 'string' && profileData.primary_specialization.includes(',') 
                  ? profileData.primary_specialization.split(',').map(s => s.trim())
                  : [profileData.primary_specialization]) : []),
            technical_specializations: profileData.technical_specializations || '',
            // Handle sub_specialties - convert string to array if needed
            sub_specialties: Array.isArray(profileData.sub_specialties)
              ? profileData.sub_specialties
              : (profileData.sub_specialties ? (typeof profileData.sub_specialties === 'string' && profileData.sub_specialties.includes(',')
                  ? profileData.sub_specialties.split(',').map(s => s.trim())
                  : [profileData.sub_specialties]) : []),
            services_offered: profileData.services_offered || {
              inspection_type: [],
              industries_served: [],
              methodologies: [],
              notable_projects: []
            },
            quality_methodologies: profileData.quality_methodologies || profileData.methodologies_standards || '',
            methodologies_standards: profileData.methodologies_standards || profileData.quality_methodologies || '',
            calibrated_tools_portfolio: profileData.calibrated_tools_portfolio || profileData.tools_equipment || '',
            tools_equipment: profileData.tools_equipment || profileData.calibrated_tools_portfolio || '',
            industries_served: profileData.industries_served || [],
            inspection_types: profileData.inspection_types || [],
            notable_clients: profileData.notable_clients || '',
            experience: profileData.experience && profileData.experience.length > 0
              ? profileData.experience.map(exp => ({
                  role_name: exp.role_name || '',
                  // Map seniority (from backend) back to level (for frontend)
                  level: exp.seniority || exp.level || 'Mid Level',
                  year_of_experience: exp.year_of_experience || '',
                  notable_projects_clients: exp.notable_projects_clients || profileData.notable_clients || ''
                }))
              : initialOtherInfo.experience,
            education: profileData.education && profileData.education.length > 0 
              ? profileData.education.map(edu => {
                  // Parse institution field - it may contain "Institution - Field" format
                  const institutionValue = edu.institution || edu.university || '';
                  let institution = institutionValue;
                  let specialization = '';
                  
                  // Check if institution contains " - " separator (format: "Institution - Field")
                  if (institutionValue.includes(' - ')) {
                    const parts = institutionValue.split(' - ');
                    institution = parts[0] || '';
                    specialization = parts.slice(1).join(' - ') || ''; // Join in case there are multiple " - "
                  } else {
                    // If no separator, check if it might be just a specialization/field
                    // (This handles the case where only specialization was saved)
                    specialization = '';
                    institution = institutionValue;
                  }
                  
                  return {
                    degree: edu.degree || '',
                    specialization: specialization,
                    institution: institution,
                    university: institution, // Keep for backward compatibility
                    year: edu.year || ''
                  };
                })
              : initialOtherInfo.education,
            certifications: profileData.certifications && profileData.certifications.length > 0
              ? profileData.certifications.map(cert => ({
                  details: cert.details || cert.cert_name || '',
                  cert_name: cert.cert_name || cert.details || '',
                  issuer: cert.issuer || cert.authority || '',
                  authority: cert.authority || cert.issuer || '',
                  valid_until: cert.valid_until || cert.validity || '',
                  validity: cert.validity || cert.valid_until || '',
                  attachment: normalizeFilePath(cert.attachment || '')
                }))
              : initialOtherInfo.certifications,
            // Training programs - backend stores as numbers (index + 1), map back to strings
            training_programs: (() => {
              if (!Array.isArray(profileData.training_programs) || profileData.training_programs.length === 0) {
                return [];
              }
              const mapped = profileData.training_programs.map(tp => {
                // If it's already a string, check if it exists in options
                if (typeof tp === 'string') {
                  return trainingProgramOptions.some(opt => opt.value === tp) ? tp : null;
                }
                // If it's a number, map it back to string using trainingProgramOptions
                // Numbers are stored as index + 1 (1-based)
                const index = typeof tp === 'number' ? tp - 1 : -1;
                if (index >= 0 && index < trainingProgramOptions.length) {
                  return trainingProgramOptions[index].value;
                }
                return null;
              }).filter(tp => tp !== null);
              console.log('Loading training_programs:', {
                fromBackend: profileData.training_programs,
                mapped: mapped,
                trainingProgramOptions: trainingProgramOptions
              });
              return mapped;
            })(),
            testimonials: profileData.testimonials && profileData.testimonials.length > 0
              ? profileData.testimonials.map(test => ({
                  details: test.details || '',
                  attachment: normalizeFilePath(test.attachment || '')
                }))
              : initialOtherInfo.testimonials,
            specializations: profileData.specializations || [],
            internal_status: profileData.internal_status || 'PENDING SUBMISSION',
            government_id_proof: normalizeFilePath(profileData.government_id_proof || ''),
            address_proof: normalizeFilePath(profileData.address_proof || ''),
            tax_id: profileData.tax_id || '',
            accept_nda: profileData.accept_nda || false,
            accept_ethical_conduct: profileData.accept_ethical_conduct || false,
            pricing_model: profileData.pricing_model || 'Per Day (Monday)',
            settlement_currency: profileData.settlement_currency || 'USD - US Dollar ($)',
            payout_frequency: profileData.payout_frequency || 'Weekly Settlement',
            routing_iban_details: profileData.routing_iban_details || profileData.iban_account_number || '',
            iban_account_number: profileData.iban_account_number || profileData.routing_iban_details || '',
            swift_bic_code: profileData.swift_bic_code || profileData.ifsc_swift_bic || '',
            ifsc_swift_bic: profileData.ifsc_swift_bic || profileData.swift_bic_code || '',
            // Bank details are stored at root level in bank_details object, not in profile_data
            account_holder_name: bankDetails?.account_holder || '',
            bank_name: bankDetails?.bank_name || '',
            branch_name: bankDetails?.branch_name || '',
            ifsc_code: bankDetails?.ifsc_code || '',
          });

          // Set file URLs if they exist
          if (profileData.government_id_proof) {
            setGovernmentIdProofUrl(normalizeFilePath(profileData.government_id_proof));
          }
          if (profileData.address_proof) {
            setAddressProofUrl(normalizeFilePath(profileData.address_proof));
          }
        } else {
          // Reset to initial state if no profile_data exists
          setOtherInfo(initialOtherInfo);
        }

      }
    } catch (error) {
      console.error('Error loading profile:', error);
      // Profile doesn't exist yet, that's OK
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (name, selected) => {
    const value = selected ? selected.value : '';
    
    if (name === 'country') {
      setForm(prev => ({ ...prev, country: value, state: '', city: '' }));
      if (value) updateListsForCountry(value);
    } else if (name === 'state') {
      setForm(prev => {
        const updated = { ...prev, state: value, city: '' };
        if (value && updated.country) {
          updateListsForState(updated.country, value);
        }
        return updated;
      });
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  // File upload function with validation
  const uploadFile = async (file, fileType) => {
    try {
      // File validation
      if (!file) {
        throw new Error('No file selected');
      }

      // File size validation (200MB limit as per backend)
      const maxSize = 200 * 1024 * 1024; // 200MB in bytes
      if (file.size > maxSize) {
        throw new Error(`File size exceeds 200MB limit. Please upload a smaller file.`);
      }

      // File type validation based on fileType
      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const allowedDocTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      
      if (fileType === 'profile_picture') {
        if (!allowedImageTypes.includes(file.type)) {
          throw new Error('Profile picture must be an image file (JPG, PNG, GIF, or WEBP)');
        }
      } else if (fileType === 'resume') {
        if (!allowedDocTypes.includes(file.type)) {
          throw new Error('Resume must be a PDF or Word document (PDF, DOC, or DOCX)');
        }
      } else if (fileType === 'certification' || fileType === 'testimonial') {
        // Allow both images and documents for certifications and testimonials
        if (!allowedImageTypes.includes(file.type) && !allowedDocTypes.includes(file.type)) {
          throw new Error('File must be an image (JPG, PNG, GIF, WEBP) or document (PDF, DOC, DOCX)');
        }
      }

      // Get company name or user name for folder path
      const companyName = form.company_name || form.name || 'quality_inspector';
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'quality'); // Backend expects 'quality', not 'quality_inspector'
      formData.append('companyName', companyName);
      formData.append('fileType', fileType);

      const response = await CommonService.post('/upload/single', formData);
      if (response && response.success && response.data) {
        // Return the access path (relative URL path) from the response
        // accessPath format: /media/quality/{companyName}/{fileName} (backend uses 'quality' category)
        let accessPath = response.data.accessPath || response.data.filePath || '';
        
        // Remove base URL if present (should be relative path)
        if (accessPath) {
          // Remove http://localhost:3000 or similar base URLs
          accessPath = accessPath.replace(/^https?:\/\/[^\/]+/, '');
          // Ensure it starts with /
          if (!accessPath.startsWith('/')) {
            accessPath = `/${accessPath}`;
          }
        }
        return accessPath;
      }
      throw new Error(response?.message || 'Upload failed');
    } catch (error) {
      console.error('File upload error:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.[0] || error?.message || `Failed to upload ${fileType}`;
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      // Reset file input if no file selected
      if (e.target) {
        e.target.value = '';
      }
      return;
    }

    try {
      setUploading(true);
      const fileUrl = await uploadFile(file, 'profile_picture');
      setProfilePictureUrl(fileUrl);
      toast.success('Profile picture uploaded successfully');
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      // Reset file input on error
      if (e.target) {
        e.target.value = '';
      }
    } finally {
      setUploading(false);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      // Reset file input if no file selected
      if (e.target) {
        e.target.value = '';
      }
      return;
    }

    try {
      setUploading(true);
      const fileUrl = await uploadFile(file, 'resume');
      setResumeUrl(fileUrl);
      toast.success('Resume uploaded successfully');
    } catch (error) {
      console.error('Error uploading resume:', error);
      // Reset file input on error
      if (e.target) {
        e.target.value = '';
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCertificationFileUpload = async (index, e) => {
    const file = e.target?.files?.[0] || (e.target && e.target.files && e.target.files[0]);
    if (!file) {
      return;
    }

    try {
      setUploading(true);
      const fileUrl = await uploadFile(file, 'certification');
      setOtherInfo(prev => ({
        ...prev,
        certifications: prev.certifications.map((cert, i) => 
          i === index ? { ...cert, attachment: fileUrl } : cert
        )
      }));
      toast.success('Certification file uploaded successfully');
    } catch (error) {
      console.error('Error uploading certification file:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleTestimonialFileUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) {
      // Reset file input if no file selected
      if (e.target) {
        e.target.value = '';
      }
      return;
    }

    try {
      setUploading(true);
      const fileUrl = await uploadFile(file, 'testimonial');
      setOtherInfo(prev => ({
        ...prev,
        testimonials: prev.testimonials.map((test, i) => 
          i === index ? { ...test, attachment: fileUrl } : test
        )
      }));
      toast.success('Testimonial file uploaded successfully');
    } catch (error) {
      console.error('Error uploading testimonial file:', error);
      // Reset file input on error
      if (e.target) {
        e.target.value = '';
      }
    } finally {
      setUploading(false);
    }
  };

  // File upload refs for testimonials
  const testimonialFileRefs = useRef({});

  const handleGovernmentIdProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      if (e.target) {
        e.target.value = '';
      }
      return;
    }

    try {
      setUploading(true);
      const fileUrl = await uploadFile(file, 'government_id_proof');
      setGovernmentIdProofUrl(fileUrl);
      setOtherInfo(prev => ({ ...prev, government_id_proof: fileUrl }));
      toast.success('Government ID proof uploaded successfully');
    } catch (error) {
      console.error('Error uploading government ID proof:', error);
      if (e.target) {
        e.target.value = '';
      }
    } finally {
      setUploading(false);
    }
  };

  const handleAddressProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      if (e.target) {
        e.target.value = '';
      }
      return;
    }

    try {
      setUploading(true);
      const fileUrl = await uploadFile(file, 'address_proof');
      setAddressProofUrl(fileUrl);
      setOtherInfo(prev => ({ ...prev, address_proof: fileUrl }));
      toast.success('Address proof uploaded successfully');
    } catch (error) {
      console.error('Error uploading address proof:', error);
      if (e.target) {
        e.target.value = '';
      }
    } finally {
      setUploading(false);
    }
  };

  // Add/Remove functions for arrays
  const addEducation = () => {
    setOtherInfo(prev => ({
      ...prev,
      education: [...prev.education, {
        degree: '',
        specialization: '',
        institution: '',
        university: '',
        year: ''
      }]
    }));
  };

  const removeEducation = (index) => {
      setOtherInfo(prev => ({
        ...prev,
      education: prev.education.length > 1 
        ? prev.education.filter((_, i) => i !== index)
        : prev.education
      }));
  };

  const updateEducation = (index, field, value) => {
    setOtherInfo(prev => ({
      ...prev,
      education: prev.education.map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  // Helper functions for Specializations (pill tags)
  const handleAddSpecialization = (specialization) => {
    if (specialization && !otherInfo.specializations.includes(specialization)) {
      setOtherInfo(prev => ({
        ...prev,
        specializations: [...prev.specializations, specialization]
      }));
    }
  };

  const handleRemoveSpecialization = (index) => {
    setOtherInfo(prev => ({
      ...prev,
      specializations: prev.specializations.filter((_, i) => i !== index)
    }));
  };

  // Helper functions for Training Programs (pill tags)
  const handleAddTrainingProgram = (program) => {
    if (program && !otherInfo.training_programs.includes(program)) {
      setOtherInfo(prev => ({
        ...prev,
        training_programs: [...prev.training_programs, program]
      }));
    }
  };

  const handleRemoveTrainingProgram = (index) => {
    setOtherInfo(prev => ({
      ...prev,
      training_programs: prev.training_programs.filter((_, i) => i !== index)
    }));
  };

  const addExperience = () => {
    setOtherInfo(prev => ({
      ...prev,
      experience: [...prev.experience, {
        role_name: '',
        level: 'Mid Level',
        year_of_experience: '',
        notable_projects_clients: ''
      }]
    }));
  };

  const removeExperience = (index) => {
      setOtherInfo(prev => ({
        ...prev,
      experience: prev.experience.length > 1 
        ? prev.experience.filter((_, i) => i !== index)
        : prev.experience
      }));
  };

  const updateExperience = (index, field, value) => {
    setOtherInfo(prev => ({
      ...prev,
      experience: prev.experience.map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const addCertification = () => {
    setOtherInfo(prev => ({
      ...prev,
      certifications: [...prev.certifications, {
        cert_name: '',
        issuer: '',
        valid_until: '',
        attachment: ''
      }]
    }));
  };

  const removeCertification = (index) => {
      setOtherInfo(prev => ({
        ...prev,
      certifications: prev.certifications.length > 1 
        ? prev.certifications.filter((_, i) => i !== index)
        : prev.certifications
      }));
  };

  const updateCertification = (index, field, value) => {
    setOtherInfo(prev => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) => {
        if (i === index) {
          const updated = { ...cert, [field]: value };
          // Sync issuer and authority fields
          if (field === 'issuer') {
            updated.authority = value;
          } else if (field === 'authority') {
            updated.issuer = value;
          }
          // Sync valid_until and validity fields
          if (field === 'valid_until') {
            updated.validity = value;
          } else if (field === 'validity') {
            updated.valid_until = value;
          }
          return updated;
        }
        return cert;
      })
    }));
  };

  const addTestimonial = () => {
    setOtherInfo(prev => ({
      ...prev,
      testimonials: [...prev.testimonials, {
        details: '',
        attachment: ''
      }]
    }));
  };

  const removeTestimonial = (index) => {
    if (otherInfo.testimonials.length > 1) {
      setOtherInfo(prev => ({
        ...prev,
        testimonials: prev.testimonials.filter((_, i) => i !== index)
      }));
    }
  };

  const updateTestimonial = (index, field, value) => {
    setOtherInfo(prev => ({
      ...prev,
      testimonials: prev.testimonials.map((test, i) => 
        i === index ? { ...test, [field]: value } : test
      )
    }));
  };

  // Build API payload
  // tabToSave: '1' for first tab only, '2' for second tab only
  const buildApiPayload = (tabToSave = '1') => {
    // Extract file names from access paths or full URLs
    const payload = {};

    // First tab data - only include if saving first tab
    if (tabToSave === '1') {
      payload.name = form.name || '';
      payload.company_name = form.company_name || '';
      // Email validation: send null if empty, otherwise send the email
      payload.email = form.email && form.email.trim() ? form.email.trim() : null;
      payload.phone = form.phone || '';
      payload.address = form.address || '';
      // Ensure parseInt doesn't return NaN - use null if invalid
      payload.pincode = form.pincode && !isNaN(parseInt(form.pincode)) ? parseInt(form.pincode) : null;
      payload.country_id = form.country && !isNaN(parseInt(form.country)) ? parseInt(form.country) : null;
      payload.state_id = form.state && !isNaN(parseInt(form.state)) ? parseInt(form.state) : null;
      payload.city_id = form.city && !isNaN(parseInt(form.city)) ? parseInt(form.city) : null;
      payload.gst_no = form.gst_no || '';
      // Handle languages - extract IDs from objects if needed
      const languageIds = form.languages.map(lang => typeof lang === 'object' ? lang.id : lang).filter(id => !isNaN(id));
      payload.language_id_str = languageIds.length > 0 ? languageIds.join(',') : null;
      
      // Store the accessPath (relative path like /media/quality/{companyName}/{fileName})
      payload.profile_picture = profilePictureUrl || null;
      payload.profile_picture_file_name = getFileNameFromPath(profilePictureUrl) || null;
      payload.bio = form.bio || '';
      // Resume must be a string, not null
      payload.resume = resumeUrl || null;
      payload.resume_file_name = getFileNameFromPath(resumeUrl) || null;
      payload.alternate_contact = form.alternate_contact || '';
      
      // Build profile_data object properly by merging all fields
      // Note: Only include fields that are in the validator schema
      // Extract time_zone value if it's an object (from Select component)
      // Ensure it's always a string
      let timeZoneValue = '';
      if (form.time_zone) {
        if (typeof form.time_zone === 'object' && form.time_zone !== null) {
          timeZoneValue = String(form.time_zone.value || form.time_zone.label || '');
        } else {
          timeZoneValue = String(form.time_zone);
        }
      }
      
      const profileDataObj = {
        profile_type: form.profile_type || 'Individual Professional',
        time_zone: timeZoneValue, // Must be a string, not an object
        preferred_method: form.preferred_method || 'WhatsApp',
        professional_headline: form.professional_headline || '',
        current_status: form.current_status || 'Available',
        radius: form.radius || '',
        geographic_work_limit: form.geographic_work_limit || 'State',
        primary_port_focus: form.primary_port_focus || '',
        multi_location_audits: form.multi_location_audits || '',
      };
      
      // Add languages if they exist
      if (form.languages.length > 0) {
        profileDataObj.languages = form.languages.map(lang => typeof lang === 'object' ? lang : { id: lang, proficiency: 'Basic' });
      }
      
      // Add state_text and city_text if they exist
      if (form.state_text || form.city_text) {
        profileDataObj.state_text = form.state_text || '';
        profileDataObj.city_text = form.city_text || '';
      }
      
      payload.profile_data = profileDataObj;
    }

    // Second tab data - only include if saving second tab
    if (tabToSave === '2') {
      // Build profile_data object with ONLY second tab fields
      // Do NOT include first tab fields - backend will preserve existing first tab data automatically
      // Only include fields that are in the validator schema
      const profileDataObj = {
        // Second tab fields only - matching validator schema
        primary_specialization: Array.isArray(otherInfo.primary_specialization) 
          ? otherInfo.primary_specialization.join(', ') 
          : (otherInfo.primary_specialization || ''),
        sub_specialties: Array.isArray(otherInfo.sub_specialties)
          ? otherInfo.sub_specialties.map(item => {
              // If item is a number, find the label from options, otherwise use the item itself
              if (typeof item === 'number') {
                // subSpecializationOptions is defined in component scope
                const subSpecializationOptions = [
                  { value: 1, label: 'Pre-shipment Inspection' },
                  { value: 2, label: 'During Production Inspection' },
                  { value: 3, label: 'Final Random Inspection' },
                  { value: 4, label: 'Container Loading Inspection' },
                ];
                const option = subSpecializationOptions.find(opt => opt.value === item);
                return option ? option.label : String(item);
              }
              return String(item);
            }).join(', ')
          : (otherInfo.sub_specialties || ''),
        methodologies_standards: otherInfo.methodologies_standards || otherInfo.quality_methodologies || '',
        tools_equipment: otherInfo.tools_equipment || otherInfo.calibrated_tools_portfolio || '',
        industries_served: otherInfo.industries_served || [],
        inspection_types: otherInfo.inspection_types || [],
        // Education - include specialization/field in institution if provided
        education: (otherInfo.education || []).map(edu => {
          // Combine institution and specialization if both exist
          let institutionValue = edu.institution || edu.university || '';
          if (edu.specialization && institutionValue) {
            institutionValue = `${institutionValue} - ${edu.specialization}`;
          } else if (edu.specialization && !institutionValue) {
            institutionValue = edu.specialization;
          }
          return {
            degree: edu.degree || '',
            institution: institutionValue,
            year: edu.year || ''
          };
        }),
        // Experience - only allowed fields: role_name, seniority, year_of_experience, notable_projects_clients
        experience: (otherInfo.experience || []).map(exp => ({
          role_name: exp.role_name || '',
          seniority: exp.level || '', // Map level to seniority
          year_of_experience: exp.year_of_experience || '',
          notable_projects_clients: exp.notable_projects_clients || ''
        })),
        // Certifications - only allowed fields: cert_name, authority, validity, attachment
        certifications: (otherInfo.certifications || []).map(cert => ({
          cert_name: cert.cert_name || cert.details || '',
          authority: cert.authority || cert.issuer || '',
          validity: cert.validity || cert.valid_until || '',
          attachment: cert.attachment || ''
        })),
        // Training programs - validator expects numbers, but frontend uses strings
        // Map string values to their index in trainingProgramOptions (as numbers)
        training_programs: Array.isArray(otherInfo.training_programs) 
          ? otherInfo.training_programs.map(tp => {
              // If it's already a number, return it
              if (typeof tp === 'number') {
                return tp;
              }
              // If it's a string, find its index in trainingProgramOptions
              const index = trainingProgramOptions.findIndex(opt => opt.value === tp);
              return index >= 0 ? index + 1 : null; // Use index + 1 as ID (1-based)
            }).filter(tp => tp !== null)
          : [],
        testimonials: (otherInfo.testimonials || []).map(test => ({
          details: test.details || '',
          attachment: test.attachment || ''
        })),
        // Legacy fields for backward compatibility
        specializations: otherInfo.specializations || {},
        services_offered: otherInfo.services_offered || {
          inspection_type: [],
          industries_served: [],
          methodologies: [],
          notable_projects: []
        },
        // Compliance fields
        government_id_proof: otherInfo.government_id_proof || '',
        address_proof: otherInfo.address_proof || '',
        tax_id: otherInfo.tax_id || '',
        accept_nda: otherInfo.accept_nda || false,
        accept_ethical_conduct: otherInfo.accept_ethical_conduct || false,
        // Settlement fields - only allowed in profile_data
        pricing_model: otherInfo.pricing_model || 'Per Day (Monday)',
        settlement_currency: otherInfo.settlement_currency || 'USD - US Dollar ($)',
        payout_frequency: otherInfo.payout_frequency || 'Weekly Settlement',
        iban_account_number: otherInfo.iban_account_number || otherInfo.routing_iban_details || '',
        ifsc_swift_bic: otherInfo.ifsc_swift_bic || otherInfo.swift_bic_code || '',
      };
      
      payload.profile_data = profileDataObj;
      
      // Bank details should be at root level, not in profile_data
      // Always include bank_details object (even if empty) to ensure fields are saved
      payload.bank_details = {
        account_holder: otherInfo.account_holder_name || '',
        account_number: otherInfo.iban_account_number || otherInfo.routing_iban_details || '',
        bank_name: otherInfo.bank_name || '',
        branch_name: otherInfo.branch_name || '',
        ifsc_code: otherInfo.ifsc_code || '',
        swift_code: otherInfo.swift_bic_code || otherInfo.ifsc_swift_bic || ''
      };
    }

    return payload;
  };

  // Check if profile exists for current user
  const checkExistingProfile = async () => {
    try {
      const response = await CommonService.get('/quality-inspector/my-profile');
      if (response && response.success && response.data && response.data.quality_inspector_id) {
        return response.data.quality_inspector_id;
      }
      return null;
    } catch (error) {
      // Profile doesn't exist yet (404 or other error)
      // Check if it's a 404 error (profile not found)
      if (error.response?.status === 404 || error.response?.data?.message?.includes('not found')) {
        return null; // Profile doesn't exist, safe to create
      }
      // For other errors, log but still return null to allow creation attempt
      console.log('Error checking existing profile:', error);
      return null;
    }
  };

  // Save as Draft - saves only first tab data without strict validation
  const handleSaveDraft = async () => {
    // Prevent save if file upload is in progress
    if (uploading) {
      toast.warning('Please wait for file upload to complete before saving.');
      return false;
    }

    setLoading(true);
    try {
      // First, check if profile exists (even if qualityInspectorId is not set in state)
      let existingProfileId = qualityInspectorId;
      
      if (!existingProfileId) {
        existingProfileId = await checkExistingProfile();
        if (existingProfileId) {
          setQualityInspectorId(existingProfileId);
        }
      }

      // Build payload for first tab only (tabToSave = '1')
      const payload = buildApiPayload('1');
      
      // Remove undefined values from payload to avoid validation issues
      const cleanPayload = Object.keys(payload).reduce((acc, key) => {
        if (payload[key] !== undefined) {
          acc[key] = payload[key];
        }
        return acc;
      }, {});
      
      // Log payload for debugging (remove in production)
      console.log('Saving draft payload:', JSON.stringify(cleanPayload, null, 2));
      console.log('Payload keys:', Object.keys(cleanPayload));
      
      if (existingProfileId) {
        // Update existing profile - prevents duplicate inserts
        const response = await CommonService.put(`/quality-inspector/profile/${existingProfileId}`, cleanPayload);
        if (response && response.success) {
          toast.success('Draft saved successfully!');
          // Reload profile to get latest data including any file paths
          await loadExistingProfile();
          setLoading(false);
          return true;
        } else {
          throw new Error(response?.message || 'Failed to update draft');
        }
      } else {
        // Create new profile draft
        const response = await CommonService.post('/quality-inspector/profile', cleanPayload);
        if (response && response.success) {
          toast.success('Draft saved successfully!');
          const newProfileId = response.data?.quality_inspector_id || response.data?.id;
          if (newProfileId) {
            setQualityInspectorId(newProfileId);
          }
          // Reload profile to get latest data including any file paths
          await loadExistingProfile();
          setLoading(false);
          return true;
        } else {
          throw new Error(response?.message || 'Failed to create draft');
        }
      }
    } catch (err) {
      console.error('Save draft error:', err);
      console.error('Error response:', err.response?.data);
      
      // Show more detailed error message
      let errorMessage = 'Failed to save draft. Please try again.';
      if (err.response?.data) {
        console.log('Full error response:', JSON.stringify(err.response.data, null, 2));
        if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          // Format validation errors more clearly
          const errorDetails = err.response.data.errors.map(e => {
            if (typeof e === 'string') return e;
            if (e.path) return `${e.path.join('.')}: ${e.message || e}`;
            return e.message || JSON.stringify(e);
          });
          errorMessage = `Validation failed: ${errorDetails.join('; ')}`;
          console.log('Validation errors:', errorDetails);
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
      setLoading(false);
      return false;
    }
  };

  // Save as Draft - saves only second tab data without strict validation
  const handleSaveDraftTab2 = async () => {
    // Prevent save if file upload is in progress
    if (uploading) {
      toast.warning('Please wait for file upload to complete before saving.');
      return false;
    }

    setLoading(true);
    try {
      // First, check if profile exists (even if qualityInspectorId is not set in state)
      let existingProfileId = qualityInspectorId;
      
      if (!existingProfileId) {
        existingProfileId = await checkExistingProfile();
        if (existingProfileId) {
          setQualityInspectorId(existingProfileId);
        }
      }

      // If no profile exists, warn user to save tab 1 first
      if (!existingProfileId) {
        toast.warning('Please save Tab 1 first before saving Tab 2. Creating a new profile with Tab 2 data only.');
      }

      // Build payload for second tab only (tabToSave = '2')
      // This includes all second tab fields in profile_data
      const payload = buildApiPayload('2');
      
      // Remove undefined values from payload to avoid validation issues
      const cleanPayload = Object.keys(payload).reduce((acc, key) => {
        if (payload[key] !== undefined) {
          acc[key] = payload[key];
        }
        return acc;
      }, {});
      
      // Log payload for debugging (remove in production)
      console.log('Saving draft payload (Tab 2):', JSON.stringify(cleanPayload, null, 2));
      console.log('Payload keys:', Object.keys(cleanPayload));
      console.log('Existing Profile ID:', existingProfileId);
      
      if (existingProfileId) {
        // Update existing profile - backend will merge profile_data with existing data
        // This prevents duplicate inserts and preserves all existing data
        const response = await CommonService.put(`/quality-inspector/profile/${existingProfileId}`, cleanPayload);
        if (response && response.success) {
          toast.success('Draft saved successfully! All Tab 2 information has been updated.');
          // Reload profile to get latest data including any file paths
          await loadExistingProfile();
          setLoading(false);
          return true;
        } else {
          throw new Error(response?.message || 'Failed to update draft');
        }
      } else {
        // Create new profile draft (shouldn't happen often, but handle it)
        // Note: This will create a profile with only Tab 2 data
        const response = await CommonService.post('/quality-inspector/profile', cleanPayload);
        if (response && response.success) {
          toast.success('Draft saved successfully!');
          const newProfileId = response.data?.quality_inspector_id || response.data?.id;
          if (newProfileId) {
            setQualityInspectorId(newProfileId);
          }
          // Reload profile to get latest data including any file paths
          await loadExistingProfile();
          setLoading(false);
          return true;
        } else {
          throw new Error(response?.message || 'Failed to create draft');
        }
      }
    } catch (err) {
      console.error('Save draft error (Tab 2):', err);
      console.error('Error response:', err.response?.data);
      
      // Show more detailed error message
      let errorMessage = 'Failed to save draft. Please try again.';
      if (err.response?.data) {
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          errorMessage = err.response.data.errors.map(e => e.message || e).join(', ');
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
      setLoading(false);
      return false;
    }
  };

  const handleSave = async () => {
    // Prevent save if file upload is in progress
    if (uploading) {
      toast.warning('Please wait for file upload to complete before saving.');
      return false;
    }

    setLoading(true);
    try {
      // Validate required fields from first tab (required for both tabs)
      if (!form.name || !form.name.trim()) {
        toast.error('Please enter Contact Name');
        setLoading(false);
        return false;
      }
      if (!form.company_name || !form.company_name.trim()) {
        toast.error('Please enter Name of the organization');
        setLoading(false);
        return false;
      }
      if (!form.email || !form.email.trim()) {
        toast.error('Please enter Email');
        setLoading(false);
        return false;
      }
      if (!form.phone || !form.phone.trim()) {
        toast.error('Please enter Phone Number');
        setLoading(false);
        return false;
      }

      // Validate second tab fields if we're on the second tab
      if (activeTab === '2') {
        // Validate primary specialization if required
        const primarySpec = Array.isArray(otherInfo.primary_specialization) 
          ? otherInfo.primary_specialization 
          : (otherInfo.primary_specialization ? [otherInfo.primary_specialization] : []);
        if (!primarySpec || primarySpec.length === 0) {
          toast.error('Please select at least one Primary Specialization');
          setLoading(false);
          return false;
        }
        // Validate inspection types if required
        // Inspection types are stored in services_offered.inspection_type
        const inspectionTypes = otherInfo.services_offered?.inspection_type || otherInfo.inspection_types || [];
        if (!inspectionTypes || inspectionTypes.length === 0) {
          toast.error('Please select at least one Inspection Type');
          setLoading(false);
          return false;
        }
      }

      // First, check if profile exists (even if qualityInspectorId is not set in state)
      let existingProfileId = qualityInspectorId;
      
      if (!existingProfileId) {
        existingProfileId = await checkExistingProfile();
        if (existingProfileId) {
          setQualityInspectorId(existingProfileId);
        }
      }

      // Build payload based on which tab is active
      // First tab: only first tab data (tabToSave = '1')
      // Second tab: only second tab data (tabToSave = '2')
      // Backend will preserve existing data for fields not included in the payload
      const payload = buildApiPayload(activeTab);
      
      // Log payload for debugging (remove in production)
      console.log('Saving payload:', JSON.stringify(payload, null, 2));
      console.log('Active tab:', activeTab);
      
      if (existingProfileId) {
        // Update existing profile - prevents duplicate inserts
        const response = await CommonService.put(`/quality-inspector/profile/${existingProfileId}`, payload);
        if (response && response.success) {
          toast.success('Profile updated successfully!');
          // Reload profile to get latest data including any file paths
          await loadExistingProfile();
          setLoading(false);
          return true;
        } else {
          throw new Error(response?.message || 'Failed to update profile');
        }
      } else {
        // Create new profile only if it doesn't exist
        const response = await CommonService.post('/quality-inspector/profile', payload);
        if (response && response.success) {
          toast.success('Profile created successfully!');
          const newProfileId = response.data?.quality_inspector_id || response.data?.id;
          if (newProfileId) {
            setQualityInspectorId(newProfileId);
          }
          // Reload profile to get latest data including any file paths
          await loadExistingProfile();
          setLoading(false);
          return true;
        } else {
          throw new Error(response?.message || 'Failed to create profile');
        }
      }
    } catch (err) {
      console.error('Save error:', err);
      console.error('Error response:', err.response?.data);
      
      // Handle validation errors
      let errorMessage = 'Failed to save profile. Please check all required fields.';
      
      if (err.response?.data) {
        // Check for validation errors array
        if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          errorMessage = `Validation failed: ${err.response.data.errors.join(', ')}`;
        } 
        // Check for error message
        else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
        // Check for error field
        else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
        // Check for Joi validation errors
        else if (err.response.data.details && Array.isArray(err.response.data.details)) {
          const validationErrors = err.response.data.details.map((detail) => detail.message).join(', ');
          errorMessage = `Validation failed: ${validationErrors}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
      setLoading(false);
      return false;
    }
  };

  // Helper function to handle language with proficiency
  const handleLanguageAdd = () => {
    if (selectedLanguage) {
      const langObj = { id: selectedLanguage.value, proficiency: selectedProficiency };
      setForm(prev => ({
        ...prev,
        languages: [...prev.languages, langObj]
      }));
      setSelectedLanguage(null);
      setSelectedProficiency('Basic');
    }
  };

  const handleLanguageRemove = (index) => {
    setForm(prev => ({
      ...prev,
      languages: prev.languages.filter((_, i) => i !== index)
    }));
  };

  // Tab items for antd Tabs component
  const tabItems = [
    {
      key: '1',
      label: '1. IDENTITY & SUMMARY',
      children: (
        <div className="container-fluid">
          {/* SECTION 1: BASIC INFORMATION */}
          <section className="sp-section">
            <div className="sp-subcard">
              <h6 className="sp-section-title">BASIC INFORMATION</h6>
              <p className="text-muted small mb-3">Refine profile identity and organizational structure</p>
              <div className="row g-2">
                {/* Profile Picture */}
                <div className="col-12 col-md-3 mb-3">
                  <label className="d-block text-center">
                    <div className="position-relative d-inline-block">
                      {profilePictureUrl ? (
                        <img 
                          src={profilePictureUrl.startsWith('http') 
                            ? profilePictureUrl 
                            : `${API_BASE_URL.replace('/api/v1', '')}${profilePictureUrl}`}
                          alt="Profile"
                          style={{ 
                            width: '120px', 
                            height: '120px', 
                            objectFit: 'cover',
                            borderRadius: '50%',
                            border: '2px solid #ddd',
                            cursor: 'pointer'
                          }}
                          onClick={() => profilePictureRef.current?.click()}
                        />
                      ) : (
                        <div 
                          style={{ 
                            width: '120px', 
                            height: '120px', 
                            borderRadius: '50%',
                            border: '2px solid #ddd',
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                          onClick={() => profilePictureRef.current?.click()}
                        >
                          <span>Upload</span>
                        </div>
                      )}
                      <input 
                        type="file" 
                        style={{ display: 'none' }} 
                        ref={profilePictureRef}
                        onChange={handleProfilePictureUpload}
                        accept="image/*"
                      />
                    </div>
                  </label>
                </div>
                {/* Profile Type */}
                <div className="col-12 col-md-3">
                  <Select
                    className="input-pill p-0"
                    placeholder="PROFILE TYPE"
                    value={profileTypeOptions.find(opt => opt.value === form.profile_type)}
                    onChange={(selected) => setForm(prev => ({ ...prev, profile_type: selected?.value || 'Individual Professional' }))}
                    options={profileTypeOptions}
                  />
                </div>
                {/* Full Display Name */}
                <div className="col-12 col-md-3">
                  <input
                    className="input-pill form-control"
                    placeholder="FULL DISPLAY NAME"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                  />
                </div>
                {/* Organization Name */}
                <div className="col-12 col-md-3">
                  <input
                    className="input-pill form-control"
                    placeholder="ORGANIZATION NAME"
                    name="company_name"
                    value={form.company_name}
                    onChange={onChange}
                  />
                </div>
                {/* Languages Known */}
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {form.languages.map((lang, idx) => {
                      const langOption = languageOptions.find(l => l.value === (typeof lang === 'object' ? lang.id : lang));
                      const proficiency = typeof lang === 'object' ? lang.proficiency : 'Basic';
                      return langOption ? (
                        <span key={idx} className="badge bg-secondary d-flex align-items-center gap-1">
                          {langOption.label.toUpperCase()} - {proficiency.toUpperCase()}
                          <button
                            type="button"
                            className="btn-close btn-close-white"
                            style={{ fontSize: '0.7rem' }}
                            onClick={() => handleLanguageRemove(idx)}
                          />
                        </span>
                      ) : null;
                    })}
                  </div>
                  <div className="row g-2">
                    <div className="col-12 col-md-4">
                      <Select
                        className="input-pill p-0"
                        placeholder="LANGUAGE"
                        value={selectedLanguage}
                        onChange={(selected) => setSelectedLanguage(selected)}
                        options={languageOptions}
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <Select
                        className="input-pill p-0"
                        placeholder="PROFICIENCY"
                        value={proficiencyOptions.find(opt => opt.value === selectedProficiency)}
                        onChange={(selected) => setSelectedProficiency(selected?.value || 'Basic')}
                        options={proficiencyOptions}
                      />
                    </div>
                    <div className="col-12 col-md-4 d-flex align-items-end">
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1' }}
                        onClick={handleLanguageAdd}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 2 & 3: REACH & LOCATION */}
          <section className="sp-section mt-3">
            <div className="sp-subcard">
              <h6 className="sp-section-title">REACH & LOCATION</h6>
              <p className="text-muted small mb-3">Physical base and verified contact channels</p>
              <div className="row g-2">
                <div className="col-12 col-md-4">
                  <Select
                    className="input-pill p-0"
                    placeholder="COUNTRY"
                    value={findOption(countryOptions, form.country)}
                    onChange={(selected) => handleLocationChange('country', selected)}
                    options={countryOptions}
                    isClearable
                  />
                </div>
                <div className="col-12 col-md-4">
                  <Select
                    className="input-pill p-0"
                    placeholder="STATE / PROVINCE"
                    value={findOption(stateOptions, form.state)}
                    onChange={(selected) => handleLocationChange('state', selected)}
                    options={stateOptions}
                    isClearable
                    key={`state-${form.country}-${stateOptions.length}`}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <Select
                    className="input-pill p-0"
                    placeholder="CITY"
                    value={findOption(cityOptions, form.city)}
                    onChange={(selected) => handleLocationChange('city', selected)}
                    options={cityOptions}
                    isClearable
                    key={`city-${form.state}-${cityOptions.length}`}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <input
                    className="input-pill form-control"
                    placeholder="PINCODE / ZIP"
                    name="pincode"
                    value={form.pincode}
                    onChange={onChange}
                  />
                </div>
                <div className="col-12 col-md-8">
                  <input
                    className="input-pill form-control"
                    placeholder="FULL ADDRESS"
                    name="address"
                    value={form.address}
                    onChange={onChange}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <div className="position-relative">
                    <input
                      type="email"
                      className="input-pill form-control"
                      placeholder="VERIFIED EMAIL"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                    />
                    {form.email && form.email.includes('@') && (
                      <span className="position-absolute end-0 top-50 translate-middle-y me-2 text-success">✓</span>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="position-relative">
                    <input
                      className="input-pill form-control"
                      placeholder="VERIFIED PHONE"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                    />
                    {form.phone && form.phone.length > 5 && (
                      <span className="position-absolute end-0 top-50 translate-middle-y me-2 text-success">✓</span>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <input
                    className="input-pill form-control"
                    placeholder="ALTERNATE CONTACT"
                    name="alternate_contact"
                    value={form.alternate_contact}
                    onChange={onChange}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <Select
                    className="input-pill p-0"
                    placeholder="PREFERRED CHANNEL"
                    value={preferredMethodOptions.find(opt => opt.value === form.preferred_method)}
                    onChange={(selected) => setForm(prev => ({ ...prev, preferred_method: selected?.value || 'WhatsApp' }))}
                    options={preferredMethodOptions}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <Select
                    className="input-pill p-0"
                    placeholder="LOCAL TIME ZONE"
                    value={findOption(timezoneOptions, form.time_zone)}
                    onChange={(selected) => setForm(prev => ({ ...prev, time_zone: selected?.value || '' }))}
                    options={timezoneOptions}
                    isClearable
                  />
                </div>
                <div className="col-12 col-md-4">
                  <input
                    className="input-pill form-control"
                    placeholder="GST NO"
                    name="gst_no"
                    value={form.gst_no}
                    onChange={onChange}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 4 & 5: PROFILE & AVAILABILITY */}
          <section className="sp-section mt-3">
            <div className="sp-subcard">
              <h6 className="sp-section-title">PROFILE & AVAILABILITY</h6>
              <p className="text-muted small mb-3">Professional summary, bio and geographical reach</p>
              <div className="row g-2">
                <div className="col-12">
                  <input
                    className="input-pill form-control"
                    placeholder="PROFESSIONAL HEADLINE"
                    name="professional_headline"
                    value={form.professional_headline}
                    onChange={onChange}
                  />
                </div>
                <div className="col-12">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-muted small">{form.bio.length}/2000</span>
                  </div>
                    <textarea
                      className="input-area form-control"
                    placeholder="EXPERT PROFESSIONAL BIO"
                      rows="6"
                      name="bio"
                      value={form.bio}
                      onChange={onChange}
                      maxLength={2000}
                    />
                </div>
                <div className="col-12 col-md-6">
                  <Select
                    className="input-pill p-0"
                    placeholder="STATUS"
                    value={currentStatusOptions.find(opt => opt.value === form.current_status)}
                    onChange={(selected) => setForm(prev => ({ ...prev, current_status: selected?.value || 'Available' }))}
                    options={currentStatusOptions}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    className="input-pill form-control"
                    placeholder="RADIUS (KM)"
                    name="radius"
                    value={form.radius}
                    onChange={onChange}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <Select
                    className="input-pill p-0"
                    placeholder="GEOGRAPHIC WORK LIMIT"
                    value={geographicWorkLimitOptions.find(opt => opt.value === form.geographic_work_limit)}
                    onChange={(selected) => setForm(prev => ({ ...prev, geographic_work_limit: selected?.value || 'State' }))}
                    options={geographicWorkLimitOptions}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    className="input-pill form-control"
                    placeholder="PRIMARY PORT FOCUS"
                    name="primary_port_focus"
                    value={form.primary_port_focus}
                    onChange={onChange}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    className="input-pill form-control"
                    placeholder="MULTI-LOCATION AUDITS"
                    name="multi_location_audits"
                    value={form.multi_location_audits}
                    onChange={onChange}
                  />
                </div>
                <div className="col-12">
                  <label className="file-box d-block p-2 border rounded" style={{ cursor: 'pointer' }}>
                    <div className="file-content d-flex align-items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      <span>UPLOAD FILE</span>
                    </div>
                    <div className="small text-muted mt-1">PDF, JPG (Max 10MB)</div>
                    <input 
                      type="file" 
                      style={{ display: 'none' }} 
                      ref={resumeRef}
                      onChange={handleResumeUpload}
                      accept=".pdf,.jpg,.jpeg"
                    />
                    {resumeUrl && (
                      <div className="mt-2 small text-success">✓ Resume uploaded</div>
                    )}
                  </label>
                  <div className="small text-muted mt-1">Upload your latest CV or Agency profile (PDF Only)</div>
                </div>
              </div>
            </div>
          </section>

          {/* Buttons for Personal Details Tab */}
          <div className="d-flex justify-content-between gap-3 mt-4 mb-4">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                // Reset profile functionality
                if (window.confirm('Are you sure you want to reset the profile?')) {
                  setForm(initialPersonalDetails);
                  setOtherInfo(initialOtherInfo);
                  setProfilePictureUrl('');
                  setResumeUrl('');
                }
              }}
              style={{ minWidth: '120px', padding: '12px 24px' }}
            >
              RESET PROFILE DATA
            </button>
            <div className="d-flex gap-3">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={async () => {
                  await handleSaveDraft();
                }}
                disabled={loading || uploading}
                style={{ minWidth: '120px', padding: '12px 24px' }}
              >
                {loading ? 'Saving...' : 'SAVE DRAFT PROFILE'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  const success = await handleSave();
                  if (success) {
                  setActiveTab('2');
                  }
                }}
                disabled={loading || uploading}
                style={{ backgroundColor: '#1a237e', borderColor: '#1a237e', minWidth: '120px', padding: '12px 24px' }}
              >
                NEXT
              </button>
            </div>
          </div>
        </div>
      )
    },
    {
      key: '2',
      label: '2. EXPERTISE & COMPLIANCE',
      children: (
        <div className="container-fluid">
          {/* SECTION 6: CAPABILITY MATRIX */}
          <section className="sp-section mt-3">
            <div className="sp-subcard">
              <h6 className="sp-section-title">CAPABILITY MATRIX</h6>
              <p className="text-muted mb-3" style={{ fontSize: '14px', marginLeft: '0' }}>Define the scope of services and technical standards</p>
              
              <div className="row g-3 mb-4">
                <div className="col-12 col-md-6">
                  <Select
                    className="input-pill p-0"
                    isMulti
                    placeholder="PRIMARY SPECIALIZATION *"
                    value={primarySpecializationDropdownOptions.filter(opt => 
                      Array.isArray(otherInfo.primary_specialization) 
                        ? otherInfo.primary_specialization.includes(opt.value)
                        : (otherInfo.primary_specialization === opt.value)
                    )}
                    onChange={(selected) => {
                      setOtherInfo(prev => ({
                        ...prev,
                        primary_specialization: selected ? selected.map(s => s.value) : []
                      }));
                    }}
                    options={primarySpecializationDropdownOptions}
                    isClearable
                  />
                </div>
                <div className="col-12 col-md-6">
                  <Select
                    className="input-pill p-0"
                    isMulti
                    placeholder="SUB SPECIALIZATION"
                    value={subSpecializationOptions.filter(opt => 
                      Array.isArray(otherInfo.sub_specialties)
                        ? otherInfo.sub_specialties.includes(opt.value) || otherInfo.sub_specialties.includes(opt.label)
                        : (otherInfo.sub_specialties === opt.value || otherInfo.sub_specialties === opt.label)
                    )}
                    onChange={(selected) => {
                          setOtherInfo(prev => ({
                            ...prev,
                        sub_specialties: selected ? selected.map(s => s.value || s.label) : []
                          }));
                        }}
                    options={subSpecializationOptions}
                    isClearable
                  />
                  </div>
                </div>

              <div className="mb-4">
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <Select
                      className="input-pill p-0"
                      isMulti
                      value={inspectionTypeOptions.filter(opt => otherInfo.services_offered?.inspection_type?.includes(opt.value))}
                      onChange={(selected) => {
                          setOtherInfo(prev => ({
                            ...prev,
                          services_offered: {
                            ...prev.services_offered,
                            inspection_type: selected ? selected.map(s => s.value) : []
                          }
                          }));
                        }}
                      options={inspectionTypeOptions}
                      placeholder="INSPECTION TYPE"
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <Select
                      className="input-pill p-0"
                      isMulti
                      value={industriesServedOptions.filter(opt => otherInfo.services_offered?.industries_served?.includes(opt.value))}
                      onChange={(selected) => {
                        setOtherInfo(prev => ({
                          ...prev,
                          services_offered: {
                            ...prev.services_offered,
                            industries_served: selected ? selected.map(s => s.value) : []
                          }
                        }));
                      }}
                      options={industriesServedOptions}
                      placeholder="INDUSTRIES SERVED"
                    />
                </div>
                  <div className="col-12 col-md-6">
                    <Select
                      className="input-pill p-0"
                      isMulti
                      value={methodologiesOptions.filter(opt => otherInfo.services_offered?.methodologies?.includes(opt.value))}
                      onChange={(selected) => {
                        setOtherInfo(prev => ({
                          ...prev,
                          services_offered: {
                            ...prev.services_offered,
                            methodologies: selected ? selected.map(s => s.value) : []
                          }
                        }));
                      }}
                      options={methodologiesOptions}
                      placeholder="METHODOLOGIES"
                  />
                </div>
                  <div className="col-12 col-md-6">
                    <Select
                      className="input-pill p-0"
                      isMulti
                      value={notableProjectsOptions.filter(opt => otherInfo.services_offered?.notable_projects?.includes(opt.value))}
                      onChange={(selected) => {
                        setOtherInfo(prev => ({
                          ...prev,
                          services_offered: {
                            ...prev.services_offered,
                            notable_projects: selected ? selected.map(s => s.value) : []
                          }
                        }));
                      }}
                      options={notableProjectsOptions}
                      placeholder="NOTABLE PROJECTS"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 7: PROFESSIONAL EXPERIENCE */}
          <section className="sp-section mt-3">
            <div className="sp-subcard">
              <h6 className="sp-section-title">PROFESSIONAL EXPERIENCE</h6>
              <p className="text-muted mb-3" style={{ fontSize: '14px', marginLeft: '0' }}>Add multiple professional roles and career milestones</p>
              {otherInfo.experience.map((exp, index) => (
                <div key={index} className="row g-2 mb-2 align-items-end">
                  <div className="col-12 col-md-3">
                    <input
                      className="input-pill form-control"
                      placeholder="Role"
                      value={exp.role_name}
                      onChange={(e) => updateExperience(index, 'role_name', e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-3">
                    <Select
                      className="input-pill p-0"
                      placeholder="Level"
                      value={levelOptions.find(opt => opt.value === exp.level)}
                      onChange={(selected) => updateExperience(index, 'level', selected?.value || 'Mid Level')}
                      options={levelOptions}
                    />
                  </div>
                  <div className="col-12 col-md-2">
                    <input
                      className="input-pill form-control"
                      placeholder="Years"
                      value={exp.year_of_experience}
                      onChange={(e) => updateExperience(index, 'year_of_experience', e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-3">
                      <input
                        className="input-pill form-control"
                      placeholder="NOTABLE CLIENTS / AUDITS"
                      value={exp.notable_projects_clients || ''}
                      onChange={(e) => updateExperience(index, 'notable_projects_clients', e.target.value)}
                      />
                    </div>
                  <div className="col-12 col-md-1 d-flex gap-1">
                  {index === otherInfo.experience.length - 1 && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={addExperience}
                        style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', minWidth: '30px', padding: '6px' }}
                      >
                        +
                      </button>
                    )}
                    {otherInfo.experience.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeExperience(index)}
                        style={{ minWidth: '30px', padding: '6px' }}
                      >
                        ×
                      </button>
                    )}
                    </div>
                </div>
              ))}
            </div>
          </section>

          {/* SECTION 8: ACADEMIC & CERTIFICATIONS */}
          <section className="sp-section mt-3">
            <div className="sp-subcard">
              <h6 className="sp-section-title">ACADEMIC & CERTIFICATIONS</h6>
              <p className="text-muted mb-3" style={{ fontSize: '14px', marginLeft: '0' }}>Industry credentials and educational background</p>
              
              <div className="mb-4">
                {otherInfo.education.map((edu, index) => (
                  <div key={index} className="row g-2 mb-2 align-items-end">
                    <div className="col-12 col-md-3">
                      <Select
                        className="input-pill p-0"
                        placeholder="Degree"
                        value={degreeTypeOptions.find(opt => opt.value === edu.degree)}
                        onChange={(selected) => updateEducation(index, 'degree', selected?.value || '')}
                        options={degreeTypeOptions}
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <input
                        className="input-pill form-control"
                        placeholder="Field"
                        value={edu.specialization || ''}
                        onChange={(e) => updateEducation(index, 'specialization', e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <input
                        className="input-pill form-control"
                        placeholder="Institution"
                        value={edu.institution || edu.university || ''}
                        onChange={(e) => {
                          updateEducation(index, 'institution', e.target.value);
                          updateEducation(index, 'university', e.target.value);
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-2">
                      <input
                        className="input-pill form-control"
                        placeholder="Year"
                        value={edu.year}
                        onChange={(e) => updateEducation(index, 'year', e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-md-1 d-flex gap-1">
                    {index === otherInfo.education.length - 1 && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={addEducation}
                          style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', minWidth: '30px', padding: '6px' }}
                        >
                          +
                        </button>
                      )}
                      {otherInfo.education.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeEducation(index)}
                          style={{ minWidth: '30px', padding: '6px' }}
                        >
                          ×
                        </button>
                      )}
                      </div>
                  </div>
                ))}
              </div>

              <div>
                {otherInfo.certifications.map((cert, index) => (
                <div key={index} className="mb-3">
                  <div className="row g-2 mb-2 align-items-end">
                    <div className="col-12 col-md-3">
                      <input
                        className="input-pill form-control"
                        placeholder="Certificate Name"
                        value={cert.cert_name || cert.details || ''}
                        onChange={(e) => {
                          updateCertification(index, 'cert_name', e.target.value);
                          updateCertification(index, 'details', e.target.value);
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <input
                        className="input-pill form-control"
                        placeholder="Issuer"
                        value={cert.issuer || cert.authority || ''}
                        onChange={(e) => {
                          updateCertification(index, 'issuer', e.target.value);
                          updateCertification(index, 'authority', e.target.value);
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <input
                        type="date"
                        className="input-pill form-control"
                        placeholder="Valid Until"
                        value={cert.valid_until || cert.validity || ''}
                        onChange={(e) => {
                          updateCertification(index, 'valid_until', e.target.value);
                          updateCertification(index, 'validity', e.target.value);
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-2">
                      <label className="file-box d-block" style={{ cursor: 'pointer' }}>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm w-100"
                          onClick={(e) => {
                            e.preventDefault();
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.pdf,.jpg,.jpeg,.png';
                            input.onchange = (event) => handleCertificationFileUpload(index, event);
                            input.click();
                          }}
                        >
                          {cert.attachment ? 'Change File' : 'Upload File'}
                        </button>
                        {cert.attachment && (
                          <div className="mt-1 small text-success">
                            ✓ {getFileNameFromPath(cert.attachment) || 'File uploaded'}
                          </div>
                        )}
                      </label>
                    </div>
                    <div className="col-12 col-md-1 d-flex gap-1">
                    {index === otherInfo.certifications.length - 1 && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={addCertification}
                          style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', minWidth: '30px', padding: '6px' }}
                        >
                          +
                        </button>
                      )}
                      {otherInfo.certifications.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeCertification(index)}
                          style={{ minWidth: '30px', padding: '6px' }}
                        >
                          ×
                        </button>
                      )}
                      </div>
                  </div>
                  </div>
                ))}
              </div>

              <div className="mt-4">
                <Select
                  className="input-pill p-0"
                  isMulti
                  value={trainingProgramOptions.filter(opt => otherInfo.training_programs?.includes(opt.value))}
                  onChange={(selected) => {
                    setOtherInfo(prev => ({
                      ...prev,
                      training_programs: selected ? selected.map(s => s.value) : []
                    }));
                  }}
                  options={trainingProgramOptions}
                  placeholder="Training Programs"
                />
              </div>
            </div>
          </section>

          {/* REVIEWS AND TESTIMONIALS */}
          <section className="sp-section mt-3">
            <div className="sp-subcard">
              <h6 className="sp-section-title">REVIEWS AND TESTIMONIALS</h6>
              <p className="text-muted mb-3" style={{ fontSize: '14px', marginLeft: '0' }}>Client feedback and professional endorsements</p>
              {otherInfo.testimonials.map((test, index) => (
                <div key={index} className="mb-3">
                  <div className="row g-2 mb-2">
                    <div className="col-12">
                      <textarea
                        className="form-control"
                        placeholder="Enter testimonial details..."
                        rows="3"
                        value={test.details}
                        onChange={(e) => updateTestimonial(index, 'details', e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="file-box d-block p-2 border rounded" style={{ cursor: 'pointer' }}>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={(e) => {
                            e.preventDefault();
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.pdf,.jpg,.jpeg,.png';
                            input.onchange = (event) => handleTestimonialFileUpload(index, event);
                            input.click();
                          }}
                        >
                          Attach File
                        </button>
                        {test.attachment && (
                          <div className="mt-2 small text-success">
                            ✓ File uploaded: {getFileNameFromPath(test.attachment) || 'testimonial file'}
                          </div>
                        )}
                      </label>
                    </div>
                    <div className="col-12 d-flex justify-content-end gap-2">
                      {index === otherInfo.testimonials.length - 1 && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={addTestimonial}
                          style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                        >
                          +
                        </button>
                      )}
                      {otherInfo.testimonials.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeTestimonial(index)}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* SECTION 9: COMPLIANCE PROOFS */}
          <section className="sp-section mt-3">
            <div className="sp-subcard">
              <h6 className="sp-section-title">COMPLIANCE PROOFS</h6>
              <div className="row g-2">
                <div className="col-12 col-md-6">
                  <label className="file-box d-block p-2 border rounded" style={{ cursor: 'pointer' }}>
                    <div className="file-content d-flex align-items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      <span>UPLOAD DOCUMENTATION</span>
                    </div>
                    <div className="small text-muted mt-1">PDF, JPG, PNG (10MB)</div>
                    <input 
                      type="file" 
                      style={{ display: 'none' }} 
                      ref={governmentIdProofRef}
                      onChange={handleGovernmentIdProofUpload}
                      accept=".pdf,.jpg,.jpeg"
                    />
                    {governmentIdProofUrl && (
                      <div className="mt-2 small text-success">✓ File uploaded</div>
                    )}
                  </label>
                </div>
                <div className="col-12 col-md-6">
                  <label className="file-box d-block p-2 border rounded" style={{ cursor: 'pointer' }}>
                    <div className="file-content d-flex align-items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      <span>UPLOAD DOCUMENTATION</span>
                    </div>
                    <div className="small text-muted mt-1">PDF, JPG, PNG (10MB)</div>
                    <input 
                      type="file" 
                      style={{ display: 'none' }} 
                      ref={addressProofRef}
                      onChange={handleAddressProofUpload}
                      accept=".pdf,.jpg,.jpeg"
                    />
                    {addressProofUrl && (
                      <div className="mt-2 small text-success">✓ File uploaded</div>
                    )}
                  </label>
                </div>
                <div className="col-12 col-md-6">
                  <input
                    className="input-pill form-control"
                    placeholder="TAX ID / VAT REGISTRATION"
                    value={otherInfo.tax_id}
                    onChange={(e) => setOtherInfo(prev => ({ ...prev, tax_id: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <div className="p-3 border rounded" style={{ backgroundColor: '#1a237e', color: 'white' }}>
                    <span className="fw-bold">{otherInfo.internal_status || 'PENDING SUBMISSION'}</span>
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="accept_nda"
                      checked={otherInfo.accept_nda}
                      onChange={(e) => setOtherInfo(prev => ({ ...prev, accept_nda: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="accept_nda">
                      I ACCEPT NON-DISCLOSURE TERMS
                    </label>
                  </div>
                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="accept_ethical_conduct"
                      checked={otherInfo.accept_ethical_conduct}
                      onChange={(e) => setOtherInfo(prev => ({ ...prev, accept_ethical_conduct: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="accept_ethical_conduct">
                      I ACCEPT ETHICAL CONDUCT POLICY
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 10: Settlements */}
          <section className="sp-section mt-3">
            <div className="sp-subcard">
              <h6 className="sp-section-title">SETTLEMENTS</h6>
              <div className="row g-2">
                <div className="col-12 col-md-4">
                  <Select
                    className="input-pill p-0"
                    placeholder="PRICING MODEL"
                    value={pricingModelOptions.find(opt => opt.value === otherInfo.pricing_model)}
                    onChange={(selected) => setOtherInfo(prev => ({ ...prev, pricing_model: selected?.value || 'Per Day (Monday)' }))}
                    options={pricingModelOptions}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <Select
                    className="input-pill p-0"
                    placeholder="SETTLEMENT CURRENCY"
                    value={settlementCurrencyOptions.find(opt => opt.value === otherInfo.settlement_currency)}
                    onChange={(selected) => setOtherInfo(prev => ({ ...prev, settlement_currency: selected?.value || 'USD - US Dollar ($)' }))}
                    options={settlementCurrencyOptions}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <Select
                    className="input-pill p-0"
                    placeholder="PAYOUT FREQUENCY"
                    value={payoutFrequencyOptions.find(opt => opt.value === otherInfo.payout_frequency)}
                    onChange={(selected) => setOtherInfo(prev => ({ ...prev, payout_frequency: selected?.value || 'Weekly Settlement' }))}
                    options={payoutFrequencyOptions}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    className="input-pill form-control"
                    placeholder="NAME OF THE ACCOUNT HOLDER"
                    value={otherInfo.account_holder_name || ''}
                    onChange={(e) => setOtherInfo(prev => ({ ...prev, account_holder_name: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    className="input-pill form-control"
                    placeholder="BANK NAME"
                    value={otherInfo.bank_name || ''}
                    onChange={(e) => setOtherInfo(prev => ({ ...prev, bank_name: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    className="input-pill form-control"
                    placeholder="BRANCH NAME"
                    value={otherInfo.branch_name || ''}
                    onChange={(e) => setOtherInfo(prev => ({ ...prev, branch_name: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    className="input-pill form-control"
                    placeholder="IFSC CODE"
                    value={otherInfo.ifsc_code || ''}
                    onChange={(e) => setOtherInfo(prev => ({ ...prev, ifsc_code: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    className="input-pill form-control"
                    placeholder="ROUTING / IBAN DETAILS"
                    value={otherInfo.routing_iban_details || otherInfo.iban_account_number || ''}
                    onChange={(e) => setOtherInfo(prev => ({ ...prev, routing_iban_details: e.target.value, iban_account_number: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    className="input-pill form-control"
                    placeholder="SWIFT / BIC CODE"
                    value={otherInfo.swift_bic_code || otherInfo.ifsc_swift_bic || ''}
                    onChange={(e) => setOtherInfo(prev => ({ ...prev, swift_bic_code: e.target.value, ifsc_swift_bic: e.target.value }))}
                  />
                </div>
                <div className="col-12">
                  <div className="p-3 border rounded" style={{ backgroundColor: '#d4edda', borderColor: '#c3e6cb' }}>
                    <div className="d-flex align-items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      <div>
                        <div className="fw-bold text-success">ESCROW SECURITY ACTIVE</div>
                        <div className="small text-muted">Your payments are protected by platform escrow funding technology.</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Buttons for Pro & Compliance Tab */}
          <div className="d-flex justify-content-between gap-3 mt-4 mb-4">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                // Reset profile functionality
                if (window.confirm('Are you sure you want to reset the profile?')) {
                  setForm(initialPersonalDetails);
                  setOtherInfo(initialOtherInfo);
                  setProfilePictureUrl('');
                  setResumeUrl('');
                }
              }}
              style={{ minWidth: '120px', padding: '12px 24px' }}
            >
              RESET PROFILE DATA
            </button>
            <div className="d-flex gap-3">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={async () => {
                  await handleSaveDraftTab2();
                }}
                disabled={loading || uploading}
                style={{ minWidth: '120px', padding: '12px 24px' }}
              >
                {loading ? 'Saving...' : 'SAVE DRAFT PROFILE'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setActiveTab('1')}
                style={{ minWidth: '120px', padding: '12px 24px' }}
              >
                BACK
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  await handleSave();
                }}
                disabled={loading || uploading}
                style={{ backgroundColor: '#1a237e', borderColor: '#1a237e', minWidth: '120px', padding: '12px 24px' }}
              >
                {loading ? 'Saving...' : 'SUBMIT VERIFICATION'}
              </button>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
      <div className="seller-profile container-fluid">
        <div className="row">
          <main className="col-12 sp-main">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <nav aria-label="breadcrumb" className="sp-breadcrumb">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item"><a href="/" className="breadcrumb-link">Home</a></li>
                  <li className="breadcrumb-item active" aria-current="page">Quality Inspector Profile</li>
                </ol>
              </nav>
            </div>

            <h1 className="sp-main-title">Quality Inspector Profile</h1>

            <Tabs className="sp-card" activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
          </main>
        </div>
      </div>
    </>
  );
}
